import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Check, RotateCcw } from 'lucide-react'
import { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import Card from '../Card'
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard'

interface Props {
  name: 'readonlyKey' | 'privateKey'
  disabled?: boolean
}

const ApiKeyField = ({ name, disabled }: Props) => {
  const { t } = useTranslation()
  const { setValue, watch } = useFormContext()
  const { copied, copyToClipboard } = useCopyToClipboard(
    (text: string) => !!text && text !== 'new-key',
  )

  const formValue = watch(name)

  const originalValue = useMemo(() => formValue, [])

  const action = () => {
    if (formValue === 'new-key' || copied === undefined) {
      return (
        <a
          onClick={() =>
            setValue(
              name,
              formValue === 'new-key' ? originalValue : 'new-key',
              { shouldDirty: true },
            )
          }
          className='cursor-pointer font-normal flex gap-1.5 items-center hover:text-accent-foreground'
        >
          <RotateCcw size={14} />{' '}
          {formValue === 'new-key'
            ? t('shared.undo')
            : t('dashboard.team.settings.reset-key')}
        </a>
      )
    } else {
      return (
        <span className='ml-2 text-success-foreground flex gap-1 items-center'>
          <Check size={14} /> Copied!
        </span>
      )
    }
  }

  const input = (
    <Tooltip delayDuration={150}>
      <TooltipContent>
        {t(`dashboard.team.settings.click-to-copy`)}
      </TooltipContent>
      <TooltipTrigger
        disabled={formValue === 'new-key'}
        type='button'
        className={`w-full my-2 ${
          formValue === 'new-key'
            ? 'pointer-events-none'
            : 'pointer-events-auto'
        }`}
      >
        <Card
          onClick={() => copyToClipboard(formValue)}
          className='w-full truncate py-2 px-3'
        >
          {formValue === 'new-key' ? (
            <span className='opacity-50'>
              {t(`dashboard.team.settings.new-key-notice`)}
            </span>
          ) : (
            formValue ?? <span className='opacity-50'>-</span>
          )}
        </Card>
      </TooltipTrigger>
    </Tooltip>
  )

  if (disabled) {
    return input
  }

  return (
    <Card className='mb-8 text-sm'>
      <div className='flex justify-between font-medium'>
        <span>{t(`dashboard.team.settings.title-${name}`)}</span>
        {action()}
      </div>

      {input}

      <p className='text-muted-foreground opacity-90'>
        {t(`dashboard.team.settings.description-${name}`)}
      </p>
    </Card>
  )
}

export default ApiKeyField
